import { useDemoSender, useDemoFetcherWithSwr } from "@/api";
import { userAtom } from "@/atoms/userAtom";
import { requireAuth } from "@/utils/authErrors";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

/**
 * LINE連携操作を管理するカスタムフック
 *
 * 機能:
 * - LINE連携状態の取得（SWRでキャッシュ）
 * - LINE連携開始（認可URL取得）
 * - LINE連携解除
 */
export const useLineConnection = () => {
  const user = useAtomValue(userAtom);

  // LINE連携状態を取得（SWR でキャッシュ管理）
  // 認証済みのときのみ取得を有効化（未認証時の401エラーを防止）
  const {
    data: linkStatus,
    error: linkStatusError,
    isLoading: isLoadingLinkStatus,
    mutate: refreshLinkStatus
  } = useDemoFetcherWithSwr({
    apiName: "line_connector",
    serviceName: "line",
    operationId: "getLinkStatus",
    requestParameters: undefined,
    enabled: user != null,
    swrConfiguration: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false
    }
  });

  // LINE連携開始（認可URL取得）
  const { sender: startLink, isLoading: isStartingLink } = useDemoSender({
    apiName: "line_connector",
    serviceName: "line",
    operationId: "startLink"
  });

  // LINE連携解除
  const { sender: unlinkLine, isLoading: isUnlinking } = useDemoSender({
    apiName: "line_connector",
    serviceName: "line",
    operationId: "unlink"
  });

  /**
   * LINE連携を開始（認可URLへリダイレクト）
   */
  const startLineConnection = useCallback(async () => {
    // 認証チェック
    if (!requireAuth(user)) {
      return;
    }

    try {
      const response = await startLink(undefined);
      if (response?.auth_url) {
        // LINE認可画面へリダイレクト
        window.location.href = response.auth_url;
      } else {
        console.error("Failed to get LINE auth URL");
        throw new Error("LINE連携の開始に失敗しました");
      }
    } catch (error) {
      console.error("Error starting LINE connection:", error);
      throw error;
    }
  }, [startLink, user]);

  /**
   * LINE連携を解除
   */
  const unlinkLineConnection = useCallback(async () => {
    // 認証チェック
    if (!requireAuth(user)) {
      return;
    }

    try {
      await unlinkLine(undefined);
      // 連携状態を再取得して更新
      await refreshLinkStatus();
    } catch (error) {
      console.error("Error unlinking LINE connection:", error);
      throw new Error("LINE連携の解除に失敗しました");
    }
  }, [unlinkLine, user, refreshLinkStatus]);

  /**
   * LINE連携状態を強制的に再取得
   */
  const refreshLineConnection = useCallback(async () => {
    await refreshLinkStatus();
  }, [refreshLinkStatus]);

  return {
    // 状態
    linkStatus,
    isLinked:
      linkStatus !== undefined && linkStatus !== "__fetchSuccess__" && !!linkStatus?.line_user_id,
    isDeliverySuspended:
      linkStatus !== undefined &&
      linkStatus !== "__fetchSuccess__" &&
      !!linkStatus?.line_delivery_suspended,
    isLoadingLinkStatus, // LINE連携状態の読み込み中
    isLinkProcessing: isStartingLink || isUnlinking, // LINE連携処理中（連携開始/解除）
    error: linkStatusError,

    // 操作
    startLineConnection,
    unlinkLineConnection,
    refreshLineConnection
  };
};
